import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { Outlet, useNavigate } from '@tanstack/react-router';
import type { ReactElement } from 'react';

import { ImpersonationBanner } from '../components/ImpersonationBanner';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Header } from '../components/layout/Header';
import { MutationErrorToast } from '../components/MutationErrorToast';
import { ThemeSwitcher } from '../components/ThemeSwitcher';
import { AnnouncementBanner, useBanners, useDismissBanner } from '../features/announcements';
import { settingsTabPath } from '../features/settings';
import { useCreditsWallet } from '../features/settings/api/creditsApi';
import {
    PartnerCockpitValidationState,
    useValidation,
    useValidationBannerDismissed,
    ValidationStatusBanner,
} from '../features/validation';
import { selectActiveMembership, selectIsPendingValidation, useCurrentUser } from '../lib/auth';

export function AppLayout(): ReactElement {
    const navigate = useNavigate();
    const user = useCurrentUser();
    const validation = useValidation().data?.validation;
    const validationState = validation?.state;
    const { dismissed: bannerDismissed, dismiss: dismissBanner } = useValidationBannerDismissed(
        user?.uuid,
        validation?.ticketUuid,
        validationState,
    );
    const statusBannerState =
        validationState === PartnerCockpitValidationState.validated || bannerDismissed ? undefined : validationState;
    const isPendingValidation = selectIsPendingValidation(user);
    const { data: announcements } = useBanners(!isPendingValidation);
    const dismissAnnouncement = useDismissBanner();

    const usesCredits = selectActiveMembership(user)?.usesCredits === true;
    const { data: wallet } = useCreditsWallet(usesCredits);
    const creditsBalance = usesCredits ? wallet?.availableCredits : undefined;

    const handleBannerAction = (): void => {
        // A rejected partner has nothing left to edit, so that banner's action points at support instead.
        const tab = validationState === PartnerCockpitValidationState.invalidPartner ? 'support' : 'verification';
        void navigate({ to: settingsTabPath(tab) });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
                bgcolor: 'background.default',
            }}>
            <ImpersonationBanner />
            <MutationErrorToast />
            <Header
                creditsBalance={creditsBalance}
                navItems={isPendingValidation ? [] : undefined}
                showNotifications={!isPendingValidation}
                endSlot={
                    <>
                        <ThemeSwitcher />
                        <LanguageSwitcher />
                    </>
                }
            />
            <Box component="main" sx={{ flex: 1, py: 3 }}>
                {(announcements && announcements.length > 0) || statusBannerState ? (
                    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', px: 3, pb: 3 }}>
                        <Stack spacing={2}>
                            {announcements?.map((announcement) => (
                                <AnnouncementBanner
                                    key={announcement.uuid}
                                    banner={announcement}
                                    onDismiss={() => dismissAnnouncement.mutate(announcement.uuid)}
                                />
                            ))}
                            {statusBannerState ? (
                                <ValidationStatusBanner
                                    status={statusBannerState}
                                    onAction={handleBannerAction}
                                    onClose={dismissBanner}
                                />
                            ) : undefined}
                        </Stack>
                    </Box>
                ) : undefined}
                <Outlet />
            </Box>
        </Box>
    );
}
